import { panelType } from '@bimser/common';
import { BCCard, BCDataGrid, BCExtremeGridEntities, findMessage } from '@bimser/components';
import { IDictionary, MLHelper } from '@bimser/core';
import IconAction from '@bimser/icons/16/action';
import IconTaskApprove from '@bimser/icons/16/task-approve';
import GetIcon16ByName from '@bimser/icons/lib/getIcon16ByName';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { TileTypes } from 'modules/dashboard/entities/widgets/TileTypes';
import React from 'react';
import Style from '../../assets/style.scss';
import IProcessDetailsProps from '../../entities/widgets/ProcessDetailsProps';
import useProcessDataSource from '../../hooks/useProcessDataSource';
import { dataTypeOfProcessStructureType } from '../../utils';
const cx = classNames.bind(Style);
dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

const emptyView = require('../../../../assets/jobs-empty.svg');

function isWorkspaceOneBrowser() {
	const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;

	if (typeof userAgent === 'string') {
		return (
			(userAgent.includes('iPad') || userAgent.includes('iPhone')) &&
			!userAgent.includes('Safari')
		);
	}
}

export default (props: IProcessDetailsProps) => {
	const bcDataGridRef = React.useRef<BCDataGrid.Entities.BCDataGridRef>();
	const { dataSource, structure } = useProcessDataSource(
		props.selectedProcess,
		props.category,
		bcDataGridRef
	);

	const datetimeCalculateDisplayValue = (row, item) => {
		const getter = item.name;
		const date = dayjs(row[getter]);

		if (date.isValid()) {
			if (getter === 'RequestDate' || getter === 'ProcessStartDate') {
				return date.fromNow();
			}

			return date.format(item.format || 'L LTS');
		}

		return '';
	};

	const GridIconCell = React.useMemo(
		() => (e: any) => (
			<span>
				<IconTaskApprove />
			</span>
		),
		[]
	);

	const onColumnMenuClick = (args: {
		key: string;
		column: BCDataGrid.Entities.IRemoteColumnProps;
		row: IDictionary<any>;
	}) => {
		props.onClickItem({
			panelId: props.panelId,
			panelType: panelType.startupLayout,
			data: {
				...args.row,
				fastEventId: args.key
			}
		});
	};
	const getColumnMenuItems = (args): BCDataGrid.Entities.IMenuBoxItem[] => {
		const events = JSON.parse(args.data.Events);
		if (Array.isArray(events) && events.length) {
			return events
				.filter(event => event.FastApprovalEnabled && event.Visible)
				.map<BCDataGrid.Entities.IMenuBoxItem>(e => {
					const Icon = GetIcon16ByName(e.Icon, IconAction);
					return {
						key: e.Id,
						label: MLHelper.getMLText(e.Description),
						icon: <Icon />,
						disabled: !e.Enable
					};
				});
		}
		return [];
	};

	const getFastApproveMenuProps = (): BCDataGrid.Entities.IColumnProps => {
		return {
			cellComponent: BCDataGrid.Components.MenuBox,
			editorOptions: {
				getItems: getColumnMenuItems,
				onClick: onColumnMenuClick
			}
		};
	};
	const columns = React.useMemo((): BCDataGrid.Entities.IRemoteColumnProps[] => {
		if (structure?.length) {
			const result: BCDataGrid.Entities.IRemoteColumnProps[] = structure.map(
				(item, index) => {
					const shouldDisplay = index < 3;

					const dataType = dataTypeOfProcessStructureType[item.type];

					let column: BCDataGrid.Entities.IRemoteColumnProps = {
						name: item.name,
						dataField: item.name,
						caption: MLHelper.getMLText(item.captionML),
						dbType: BCExtremeGridEntities.ItemType.String,
						dataType,
						allowHiding: !shouldDisplay,
						hidingPriority: shouldDisplay ? null : index,
						width: shouldDisplay ? '30%' : null,
						format: item.displayFormat ?? undefined,
						alignment: 'left'
					};

					if (dataType === 'datetime') {
						column.calculateDisplayValue = e => datetimeCalculateDisplayValue(e, item);
					}

					if (index === 0 && props.category === TileTypes.Waiting) {
						column = {
							...column,
							...getFastApproveMenuProps()
						};
					}

					return column;
				}
			);

			return [
				{
					dbType: BCExtremeGridEntities.ItemType.String,
					name: 'icon',
					dataField: 'icon',
					caption: '',
					hidingPriority: null,
					allowHiding: false,
					width: '30px',
					cellComponent: GridIconCell
				},
				...result
			];
		}

		return [];
	}, [props.selectedProcess, structure]);

	React.useEffect(() => {
		if (bcDataGridRef.current) {
			if (props.loading || !columns.length) {
				bcDataGridRef.current.instance().beginCustomLoading('');
			} else if (!props.loading && columns.length) {
				bcDataGridRef.current.instance().endCustomLoading();
			}
		}
	}, [props.loading, columns.length, bcDataGridRef.current]);

	const renderCustomEmpty = () => (
		<div
			data-selenium-id="dashboardProcessDetailEmptyView"
			className={cx({ emptyView: true, inCard: true, hasDefaultPadding: true })}
		>
			<img
				src={emptyView.default}
				className={Style.emptyViewImg}
			/>
			<span className={Style.emptyViewTitle}>
				{findMessage.get(props.selectedProcess ? '102969' : '102971')}
			</span>
			<span className={Style.emptyViewDesc}>
				{findMessage.get(props.selectedProcess ? '102970' : '102972')}
			</span>
		</div>
	);

	const onClickItem = React.useCallback(row => {
		props.onClickItem({
			panelId: props.panelId,
			panelType: panelType.startupLayout,
			data: row.data
		});
	}, []);

	const onCellPrepared = React.useCallback(e => {
		if (
			(e.rowType === 'data' || e.rowType === 'detailAdaptive') &&
			(e.column.dataField === 'RequestDate' || e.column.dataField === 'ProcessStartDate')
		) {
			e.cellElement.setAttribute('title', dayjs(e.value).format('L LTS'));
		}
	}, []);

	const getTitle = () => {
		const { projectCaption, projectName, flowCaption, flowName } = props.selectedProcess;
		const pCaption = MLHelper.getMLText(projectCaption) || projectName;
		const fCaption = MLHelper.getMLText(flowCaption) || flowName;

		return <span>{`${pCaption} - ${fCaption}`}</span>;
	};

	return (
		<BCCard
			bordered={false}
			className={Style.processDetailsContainer}
			seleniumId="dashboardProcessDetailCard"
			title={
				<span className={Style.widgetHeader}>
					{findMessage.get('102973') + ' '}
					{props.selectedProcess && getTitle()}
				</span>
			}
		>
			{props.selectedProcess ? (
				<BCDataGrid.Components.DataGrid
					ref={bcDataGridRef}
					name={BCDataGrid.Entities.DataGrids.ProcessDetails}
					height="100%"
					dataSource={dataSource}
					showColumnLines={false}
					showRowLines
					remoteOperations
					onCellPrepared={onCellPrepared}
					{...(isWorkspaceOneBrowser()
						? { onRowClick: onClickItem }
						: { onRowDblClick: onClickItem })}
				>
					<BCDataGrid.Components.Sorting mode={'none'} />
					<BCDataGrid.Components.Selection mode="single" />
					<BCDataGrid.Components.Scrolling
						mode="infinite"
						rowRenderingMode="virtual"
						columnRenderingMode="virtual"
					/>
					{columns.map(column => (
						<BCDataGrid.Components.Column
							key={column.dataField}
							{...column}
						/>
					))}
				</BCDataGrid.Components.DataGrid>
			) : (
				renderCustomEmpty()
			)}
		</BCCard>
	);
};
